import { useCallback, useEffect, useState } from "react";
import axiosAdmin from "../../../../api/apiAdmin";
import { successToast, errorToast } from "../../toastAlerts/ToastAlerts";
import { apiError } from "../../../../api/apiError";
import {
  BsBoxSeam,
  BsArrowClockwise,
  BsCloudDownload,
  BsExclamationTriangle,
  BsCheckCircle,
  BsShieldCheck,
  BsShieldExclamation,
  BsImage,
  BsX,
} from "react-icons/bs";

/**
 * SpaceCargo — parcels a partner warehouse pushes to us as they scan them.
 *
 * Two lists, because they answer different questions:
 *   - "received" is OUR record of every payload that arrived, including the
 *     ones we could not place. Nothing that reaches the webhook is ever lost,
 *     so this is where a missing parcel is found.
 *   - "queue" is THEIR side, read live. A parcel sitting there as Failed never
 *     reached us at all, and can be pulled in from here — their API has no
 *     replay, so this is the only recovery path.
 */
const STATES = {
  created: { label: "შექმნილია", cls: "bg-green-100 text-green-700 border-green-200" },
  duplicate: { label: "დუბლიკატი", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  unmatched: { label: "ვერ მოიძებნა", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  failed: { label: "შეცდომა", cls: "bg-red-100 text-red-700 border-red-200" },
  pending: { label: "მუშავდება", cls: "bg-blue-100 text-blue-700 border-blue-200" },
};

const REMOTE_TONES = {
  S: "text-green-600",
  A: "text-slate-500",
  P: "text-blue-600",
  E: "text-amber-600",
  F: "text-red-600",
  C: "text-slate-400",
};

const SpaceCargoPage = () => {
  const [tab, setTab] = useState("received");

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, current_page: 1, last_page: 1 });
  const [filters, setFilters] = useState({ state: "", q: "" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [remote, setRemote] = useState(null);
  const [remoteFilters, setRemoteFilters] = useState({ status: "", search: "" });
  const [remoteLoading, setRemoteLoading] = useState(false);

  const [pulling, setPulling] = useState(false);
  const [pullReport, setPullReport] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadSummary = useCallback(() => {
    axiosAdmin
      .get("/spacecargo/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { per_page: 25, page };
      if (filters.state) params.state = filters.state;
      if (filters.q.trim()) params.q = filters.q.trim();
      const { data } = await axiosAdmin.get("/spacecargo/parcels", { params });
      setRows(data.data || []);
      setMeta({
        total: data.total ?? 0,
        current_page: data.current_page ?? 1,
        last_page: data.last_page ?? 1,
      });
    } catch (err) {
      errorToast(apiError(err, "სია ვერ ჩაიტვირთა"));
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const loadRemote = useCallback(async () => {
    setRemoteLoading(true);
    try {
      const params = { count: 50 };
      if (remoteFilters.status) params.status = remoteFilters.status;
      if (remoteFilters.search.trim()) params.search = remoteFilters.search.trim();
      const { data } = await axiosAdmin.get("/spacecargo/remote", { params });
      setRemote(data);
    } catch (err) {
      setRemote(null);
      // A 429 carries Retry-After; showing it beats a generic failure, because
      // the key is shared with the webhook and hammering locks that out too.
      const after = err?.response?.data?.retry_after;
      errorToast(
        apiError(
          err,
          after ? `ლიმიტი ამოიწურა — სცადეთ ${after} წამში` : "SpaceCargo-ს პასუხი ვერ მივიღეთ"
        )
      );
    }
    setRemoteLoading(false);
  }, [remoteFilters]);

  useEffect(() => {
    if (tab === "queue") loadRemote();
  }, [tab, loadRemote]);

  const pull = async () => {
    if (
      !window.confirm(
        "SpaceCargo-დან ჩამოიტვირთება ამანათები და შეიქმნება ჩვენს სისტემაში. გავაგრძელოთ?"
      )
    )
      return;
    setPulling(true);
    setPullReport(null);
    try {
      const params = { count: 50 };
      if (remoteFilters.status) params.status = remoteFilters.status;
      if (remoteFilters.search.trim()) params.search = remoteFilters.search.trim();
      const { data } = await axiosAdmin.post("/spacecargo/pull", params);
      setPullReport(data.summary);
      successToast(
        `შეიქმნა ${data.summary.created}, დუბლიკატი ${data.summary.duplicate}, ვერ მოიძებნა ${data.summary.unmatched}`
      );
      loadRows();
      loadSummary();
      loadRemote();
    } catch (err) {
      errorToast(apiError(err, "ჩამოტვირთვა ვერ მოხერხდა"));
    }
    setPulling(false);
  };

  // Retry exists for the case the webhook cannot solve on its own: the room
  // number was mistyped or the warehouse is not mapped yet.
  const retry = async (row) => {
    const room = window.prompt(
      "ოთახის ნომერი (ცარიელი = როგორც არის):",
      row.room_number || ""
    );
    if (room === null) return;
    const country = window.prompt(
      "საწყობის ქვეყანა (ცარიელი = როგორც არის):",
      row.warehouse_country || ""
    );
    if (country === null) return;

    setRetrying(row.id);
    try {
      const body = {};
      if (room.trim()) body.room_number = room.trim();
      if (country.trim()) body.warehouse_country = country.trim();
      const { data } = await axiosAdmin.post(`/spacecargo/parcels/${row.id}/retry`, body);
      successToast(
        data.state === "created"
          ? `შეიქმნა ამანათი #${data.pack_id}`
          : `შედეგი: ${STATES[data.state]?.label || data.state}`
      );
      loadRows();
      loadSummary();
    } catch (err) {
      errorToast(apiError(err, "გამეორება ვერ მოხერხდა"));
    }
    setRetrying(null);
  };

  const Pill = ({ state }) => {
    const s = STATES[state] || STATES.pending;
    return (
      <span className={`px-2 py-0.5 rounded-full border text-[11px] font-bold ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  const inputCls =
    "h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md text-sm " +
    "bg-white dark:bg-slate-800 text-slate-800 dark:text-white";

  return (
    <div className="xl:ml-[280px] mt-[55px] bg-slate-100 dark:bg-slate-900 min-h-screen p-10 sm:p-5 pb-24">
      <div className="flex items-center gap-2 mb-4">
        <BsBoxSeam className="text-2xl text-teal_600" />
        <h1 className="text-xl font-bold dark:text-white">SpaceCargo</h1>
      </div>

      {/* Connection state — a missing key or secret is the first thing to check
          when nothing is arriving, so it is shown before anything else. */}
      {summary && (
        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          <span
            className={`px-3 py-1.5 rounded-lg border font-bold flex items-center gap-1.5 ${
              summary.configured
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-red-50 text-red-700 border-red-200"
            }`}
          >
            {summary.configured ? <BsCheckCircle /> : <BsExclamationTriangle />}
            API {summary.configured ? "დაკავშირებულია" : "არ არის კონფიგურირებული"}
          </span>
          <span
            className={`px-3 py-1.5 rounded-lg border font-bold flex items-center gap-1.5 ${
              summary.signing
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-amber-50 text-amber-800 border-amber-200"
            }`}
          >
            {summary.signing ? <BsShieldCheck /> : <BsShieldExclamation />}
            ხელმოწერა {summary.signing ? "ჩართულია" : "გამორთულია"}
          </span>
          {Object.entries(summary.states || {}).map(([k, v]) => (
            <button
              key={k}
              onClick={() => {
                setTab("received");
                setPage(1);
                setFilters((f) => ({ ...f, state: f.state === k ? "" : k }));
              }}
              className={`px-3 py-1.5 rounded-lg border dark:text-white ${
                filters.state === k
                  ? "bg-teal_600 text-white border-teal_600"
                  : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
              }`}
            >
              {STATES[k]?.label || k}: <b>{v}</b>
            </button>
          ))}
        </div>
      )}

      {summary && !summary.signing && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 mb-4 text-xs text-amber-800">
          <b>ყურადღება:</b> ხელმოწერის შემოწმება გამორთულია — webhook-ს ნებისმიერი
          გამომგზავნი შეძლებს. დააყენეთ <code>SPACECARGO_SIGNING_SECRET</code>.
        </div>
      )}

      {/* Warehouse mapping. "China" is ambiguous on our side, so what it
          resolves to has to be visible rather than buried in config. */}
      {summary?.warehouses && (
        <div className="mb-4 text-xs text-slate-500 dark:text-slate-300">
          საწყობების შესაბამისობა:{" "}
          {Object.entries(summary.warehouses).map(([k, v]) => (
            <span key={k} className="mr-3 font-mono">
              {k} → #{v}
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {[
          ["received", "მიღებული ამანათები"],
          ["queue", "SpaceCargo-ს რიგი"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 h-9 rounded-md text-sm font-bold ${
              tab === key
                ? "bg-teal_600 text-white"
                : "bg-white dark:bg-slate-700 dark:text-white text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "received" ? (
        <div className="bg-white dark:bg-slate-700 rounded-lg p-4">
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <input
              className={`${inputCls} flex-1 min-w-[200px]`}
              placeholder="ძებნა: TDS კოდი / ოთახის ნომერი"
              value={filters.q}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, q: e.target.value }));
              }}
            />
            <select
              className={inputCls}
              value={filters.state}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, state: e.target.value }));
              }}
            >
              <option value="">ყველა სტატუსი</option>
              {Object.entries(STATES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
            <button
              onClick={loadRows}
              className="h-9 px-3 rounded-md bg-slate-200 dark:bg-slate-600 dark:text-white"
              title="განახლება"
            >
              <BsArrowClockwise />
            </button>
            <span className="text-xs text-slate-500 dark:text-slate-300">
              სულ: <b>{meta.total}</b>
            </span>
          </div>

          {loading ? (
            <div className="animate-pulse h-64 bg-slate-100 dark:bg-slate-600 rounded-md" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">ჩანაწერი არ არის</p>
          ) : (
            <div className="overflow-x-auto border border-slate-200 dark:border-slate-600 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-600 text-slate-500 dark:text-slate-200">
                  <tr>
                    <th className="text-left p-2">TDS კოდი</th>
                    <th className="text-left p-2">ოთახი / მომხმარებელი</th>
                    <th className="text-left p-2">საწყობი</th>
                    <th className="text-right p-2">წონა</th>
                    <th className="text-left p-2">სტატუსი</th>
                    <th className="text-left p-2">ამანათი</th>
                    <th className="text-left p-2">მიღებულია</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-slate-100 dark:border-slate-600 dark:text-slate-200"
                    >
                      <td className="p-2 font-mono">
                        <div className="flex items-center gap-2">
                          {r.image_url && (
                            <button
                              onClick={() => setPreview(r.image_url)}
                              className="text-teal_600"
                              title="ფოტოს ნახვა"
                            >
                              <BsImage />
                            </button>
                          )}
                          {r.tds_code}
                        </div>
                      </td>
                      <td className="p-2">
                        <span className="font-mono">{r.room_number || "—"}</span>
                        {r.user && (
                          <div className="text-slate-400">
                            {[r.user.fname, r.user.lname].filter(Boolean).join(" ") ||
                              r.user.cname}{" "}
                            · #{r.user.id}
                          </div>
                        )}
                      </td>
                      <td className="p-2">{r.warehouse_country || "—"}</td>
                      <td className="p-2 text-right">{r.weight ?? "—"}</td>
                      <td className="p-2">
                        <Pill state={r.state} />
                        {r.error && (
                          <div className="text-amber-600 mt-1 max-w-[220px]">{r.error}</div>
                        )}
                      </td>
                      <td className="p-2 font-mono">
                        {r.pack ? `#${r.pack.id}` : "—"}
                      </td>
                      <td className="p-2 text-slate-400">
                        {r.created_at ? r.created_at.replace("T", " ").slice(0, 16) : "—"}
                      </td>
                      <td className="p-2">
                        {r.state !== "created" && r.state !== "duplicate" && (
                          <button
                            onClick={() => retry(r)}
                            disabled={retrying === r.id}
                            className="px-2 h-7 rounded-md bg-teal_600 text-white font-bold disabled:opacity-50"
                          >
                            {retrying === r.id ? "..." : "გამეორება"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {meta.last_page > 1 && (
            <div className="flex items-center gap-2 mt-3 text-xs dark:text-slate-200">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 h-8 rounded-md bg-slate-200 dark:bg-slate-600 disabled:opacity-40"
              >
                ←
              </button>
              <span>
                {meta.current_page} / {meta.last_page}
              </span>
              <button
                disabled={page >= meta.last_page}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 h-8 rounded-md bg-slate-200 dark:bg-slate-600 disabled:opacity-40"
              >
                →
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-700 rounded-lg p-4">
          <p className="text-xs text-slate-500 dark:text-slate-300 mb-3">
            SpaceCargo-ს რიგი, პირდაპირ მათი API-დან. სტატუსი <b>F</b> ნიშნავს, რომ
            ჩვენამდე ვერ მოაღწია — ასეთი ამანათი აქედან უნდა ჩამოიტვირთოს.
          </p>

          <div className="flex flex-wrap gap-2 items-center mb-3">
            <input
              className={`${inputCls} flex-1 min-w-[200px]`}
              placeholder="ძებნა: TDS კოდი / ოთახის ნომერი"
              value={remoteFilters.search}
              onChange={(e) =>
                setRemoteFilters((f) => ({ ...f, search: e.target.value }))
              }
            />
            <select
              className={inputCls}
              value={remoteFilters.status}
              onChange={(e) =>
                setRemoteFilters((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="">ყველა სტატუსი</option>
              {Object.entries(remote?.statuses || {}).map(([k, v]) => (
                <option key={k} value={k}>
                  {k} — {v}
                </option>
              ))}
            </select>
            <button
              onClick={loadRemote}
              className="h-9 px-3 rounded-md bg-slate-200 dark:bg-slate-600 dark:text-white"
              title="განახლება"
            >
              <BsArrowClockwise />
            </button>
            <button
              onClick={pull}
              disabled={pulling || !remote?.parcels?.length}
              className="h-9 px-4 rounded-md bg-teal_600 text-white text-sm font-bold
                         flex items-center gap-2 disabled:opacity-50"
            >
              <BsCloudDownload /> {pulling ? "იტვირთება…" : "ჩამოტვირთვა"}
            </button>
          </div>

          {pullReport && (
            <div className="rounded-md bg-slate-50 dark:bg-slate-600 border border-slate-200 dark:border-slate-500 p-3 mb-3 text-xs dark:text-slate-200">
              შეიქმნა <b>{pullReport.created}</b> · დუბლიკატი{" "}
              <b>{pullReport.duplicate}</b> · ვერ მოიძებნა{" "}
              <b className="text-amber-600">{pullReport.unmatched}</b> · შეცდომა{" "}
              <b className="text-red-600">{pullReport.failed}</b>
            </div>
          )}

          {remoteLoading ? (
            <div className="animate-pulse h-64 bg-slate-100 dark:bg-slate-600 rounded-md" />
          ) : !remote?.parcels?.length ? (
            <p className="text-sm text-slate-500 py-8 text-center">
              მათ რიგში ამანათი არ არის
            </p>
          ) : (
            <div className="overflow-x-auto border border-slate-200 dark:border-slate-600 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-600 text-slate-500 dark:text-slate-200">
                  <tr>
                    <th className="text-left p-2">TDS კოდი</th>
                    <th className="text-left p2 p-2">ოთახი</th>
                    <th className="text-left p-2">საწყობი</th>
                    <th className="text-right p-2">წონა</th>
                    <th className="text-left p-2">მათი სტატუსი</th>
                    <th className="text-left p-2">ჩვენთან</th>
                    <th className="text-left p-2">ბოლო შეცდომა</th>
                  </tr>
                </thead>
                <tbody>
                  {remote.parcels.map((p) => (
                    <tr
                      key={p.id ?? p.tdsCode}
                      className="border-t border-slate-100 dark:border-slate-600 dark:text-slate-200"
                    >
                      <td className="p-2 font-mono">
                        <div className="flex items-center gap-2">
                          {p.imageUrl && (
                            <button
                              onClick={() => setPreview(p.imageUrl)}
                              className="text-teal_600"
                              title="ფოტოს ნახვა"
                            >
                              <BsImage />
                            </button>
                          )}
                          {p.tdsCode}
                        </div>
                      </td>
                      <td className="p-2 font-mono">{p.roomNumber || "—"}</td>
                      <td className="p-2">{p.warehouseCountry || "—"}</td>
                      <td className="p-2 text-right">{p.weight ?? "—"}</td>
                      <td className={`p-2 font-bold ${REMOTE_TONES[p.status] || ""}`}>
                        {p.status} — {remote.statuses?.[p.status] || ""}
                        {p.retryCount > 0 && (
                          <span className="font-normal text-slate-400">
                            {" "}
                            ({p.retryCount} ცდა)
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        {p.mgl_state ? (
                          <Pill state={p.mgl_state} />
                        ) : (
                          <span className="text-slate-400">არ არის</span>
                        )}
                      </td>
                      <td className="p-2 text-red-600 max-w-[220px]">
                        {p.lastError || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 bg-black/70 z-[1100] flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
        >
          <button className="absolute top-4 right-4 text-white text-3xl">
            <BsX />
          </button>
          <img
            src={preview}
            alt="ამანათი"
            className="max-h-[85vh] max-w-full rounded-lg bg-white"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default SpaceCargoPage;
